/**
 * #625 — what a discount covers, the FRONTEND mirror of the backend leaf
 * `apps/backend/src/services/discount/coverage.ts` (`lineIsCovered`, `describeCoverage`,
 * `coversEverything`, `normaliseCoverage`). Both run the shared case table
 * `apps/backend/tests/fixtures/discountCoverageCases.json` (`discountCoverage.agreement.test.ts`),
 * so the words and the rule cannot drift. Plan: md/plan/625.md §5.1.
 *
 * The SERVER decides the money and sends `coverageLabel` on the rows it reads back. This copy is
 * for what it does not label: the form's live caption, the dialogs' "takes off about" estimates,
 * the catalogue list and the term-billing view's automatic discounts (plan §5.2, /qa phase 4 I2).
 */
import type { DiscountCoverage } from '../types/discount.types';
import type { InvoiceLineKind } from '../api/invoices.api';

/** Today's behaviour, and what every row held before #625 (D5): tuition, other, all products. */
export const COVERS_EVERYTHING: Readonly<DiscountCoverage> = Object.freeze({
  coversTuition: true,
  coversOther: true,
  coversAllProducts: true,
  coversUncategorised: false,
  coversCategoryIds: Object.freeze([] as string[]) as string[],
});

/** Nothing ticked: the starting point for "choose what it covers". The server refuses it as is. */
export const COVERS_NOTHING: Readonly<DiscountCoverage> = Object.freeze({
  coversTuition: false,
  coversOther: false,
  coversAllProducts: false,
  coversUncategorised: false,
  coversCategoryIds: Object.freeze([] as string[]) as string[],
});

/** The five columns off any row that carries them (a list column may arrive null). */
export function coverageFromRow(row: {
  coversTuition: boolean;
  coversOther: boolean;
  coversAllProducts: boolean;
  coversUncategorised: boolean;
  coversCategoryIds: string[] | null;
}): DiscountCoverage {
  return {
    coversTuition: row.coversTuition,
    coversOther: row.coversOther,
    coversAllProducts: row.coversAllProducts,
    coversUncategorised: row.coversUncategorised,
    coversCategoryIds: [...(row.coversCategoryIds ?? [])],
  };
}

/** The school default as the settings row stores it (`defaultCovers*`); no row yet = everything. */
export function coverageFromSchoolDefault(
  settings:
    | {
        defaultCoversTuition: boolean;
        defaultCoversOther: boolean;
        defaultCoversAllProducts: boolean;
        defaultCoversUncategorised: boolean;
        defaultCoversCategoryIds: string[] | null;
      }
    | null
    | undefined
): DiscountCoverage {
  if (!settings) return { ...COVERS_EVERYTHING, coversCategoryIds: [] };
  return {
    coversTuition: settings.defaultCoversTuition,
    coversOther: settings.defaultCoversOther,
    coversAllProducts: settings.defaultCoversAllProducts,
    coversUncategorised: settings.defaultCoversUncategorised,
    coversCategoryIds: [...(settings.defaultCoversCategoryIds ?? [])],
  };
}

/** The stored shape: "all products" clears the list and the Uncategorised tick; ids de-duplicated. */
export function normaliseCoverage(input: DiscountCoverage): DiscountCoverage {
  if (input.coversAllProducts) {
    return {
      coversTuition: input.coversTuition,
      coversOther: input.coversOther,
      coversAllProducts: true,
      coversUncategorised: false,
      coversCategoryIds: [],
    };
  }
  return {
    coversTuition: input.coversTuition,
    coversOther: input.coversOther,
    coversAllProducts: false,
    coversUncategorised: input.coversUncategorised,
    coversCategoryIds: [...new Set(input.coversCategoryIds)],
  };
}

/** The same coverage once normalised (category order ignored). */
export function sameCoverage(a: DiscountCoverage, b: DiscountCoverage): boolean {
  const x = normaliseCoverage(a);
  const y = normaliseCoverage(b);
  return (
    x.coversTuition === y.coversTuition &&
    x.coversOther === y.coversOther &&
    x.coversAllProducts === y.coversAllProducts &&
    x.coversUncategorised === y.coversUncategorised &&
    [...x.coversCategoryIds].sort().join() === [...y.coversCategoryIds].sort().join()
  );
}

/** The server's DISCOUNT_NO_COVERAGE rule, so the form can say so before saving. */
export function coverageIsEmpty(c: DiscountCoverage): boolean {
  return (
    !c.coversTuition && !c.coversOther && !c.coversAllProducts && !c.coversUncategorised && c.coversCategoryIds.length === 0
  );
}

/** THE rule, on the line's kind and category AS STORED (never re-resolved from the product). */
export function lineIsCovered(
  line: { kind: InvoiceLineKind; productCategoryId?: string | null },
  c: DiscountCoverage
): boolean {
  switch (line.kind) {
    case 'TUITION':
      return c.coversTuition;
    case 'OTHER':
      return c.coversOther;
    case 'PRODUCT':
      if (c.coversAllProducts) return true;
      return line.productCategoryId ? c.coversCategoryIds.includes(line.productCategoryId) : c.coversUncategorised;
    default: {
      const unknownKind: never = line.kind;
      void unknownKind;
      return false;
    }
  }
}

/** True when the coverage reaches every kind of line (today's behaviour). */
export function coversEverything(c: DiscountCoverage): boolean {
  return c.coversTuition && c.coversOther && c.coversAllProducts;
}

const joinList = (parts: string[]): string =>
  parts.length <= 1 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} & ${parts[parts.length - 1]}`;

/**
 * The words shown after a discount's name (D9): "Sibling discount 10% · tuition". An id not in
 * `categoryNames` (deleted, or not this school's) prints "(deleted category)", never a name.
 * Category names are NOT quoted (decided 27/10, /qa phase 2 I6): the backend prints the same
 * words on the PDF, and the fixture pins both.
 */
export function describeCoverage(c: DiscountCoverage, categoryNames: Map<string, string>): string {
  if (coversEverything(c)) return 'everything';

  const parts: string[] = [];
  if (c.coversTuition) parts.push('tuition');
  if (c.coversOther) parts.push('other charges');

  if (c.coversAllProducts) {
    parts.push('all products');
  } else {
    const names: string[] = [];
    let deleted = 0;
    for (const id of new Set(c.coversCategoryIds)) {
      const name = categoryNames.get(id);
      if (name === undefined) deleted += 1;
      else names.push(name);
    }
    if (deleted === 1) names.push('(deleted category)');
    if (deleted > 1) names.push(`(${deleted} deleted categories)`);

    if (names.length > 0) {
      parts.push(`products: ${[...names, ...(c.coversUncategorised ? ['uncategorised'] : [])].join(', ')}`);
    } else if (c.coversUncategorised) {
      parts.push('uncategorised products');
    }
  }

  return joinList(parts) || 'nothing';
}

/**
 * ` · label` after a name, or nothing for a row with no label. For the places the office CHOOSES a
 * discount (Apply Discounts, the catalogue): "everything" is worth saying there.
 */
export function coverageSuffix(label: string | null | undefined): string {
  return label ? ` · ${label}` : '';
}

/**
 * ` · label` after an APPLIED discount's name, but nothing when it covers everything (Andrew
 * 27/10, the PDF's rule, so every existing discount does not grow "· everything"). Judged on the
 * row's coverage when it has the columns; an amendment snapshot carries only the label.
 */
export function appliedCoverageSuffix(
  row: { coverageLabel?: string | null } & Partial<Pick<DiscountCoverage, 'coversTuition' | 'coversOther' | 'coversAllProducts'>>
): string {
  if (!row.coverageLabel) return '';
  const everything =
    row.coversTuition !== undefined && row.coversOther !== undefined && row.coversAllProducts !== undefined
      ? row.coversTuition && row.coversOther && row.coversAllProducts
      : row.coverageLabel === 'everything';
  return everything ? '' : ` · ${row.coverageLabel}`;
}

/** A line as the estimate sees it: its kind, its category snapshot, and its amount (qty × price). */
export interface EstimateLine {
  kind: InvoiceLineKind;
  productCategoryId?: string | null;
  amount: number;
}

const cents = (n: number): number => Math.round(n * 100) / 100;

/**
 * "Takes off about" for a NEW discount: its value against the lines it covers, capped at those
 * lines and at what the invoice has left after the discounts already on it (`alreadyOff`). An
 * ESTIMATE: where coverages overlap, the server settles each line in applied order
 * (`allocateDiscounts`), and the saved figure is what the invoice shows afterwards.
 */
export function estimateNewDiscount(
  lines: EstimateLine[],
  coverage: DiscountCoverage,
  value: number,
  isPercent: boolean,
  alreadyOff = 0
): { amount: number; coveredTotal: number; capped: boolean } {
  const coveredTotal = cents(lines.filter((l) => lineIsCovered(l, coverage)).reduce((sum, l) => sum + l.amount, 0));
  const invoiceLeft = Math.max(0, cents(lines.reduce((sum, l) => sum + l.amount, 0) - alreadyOff));
  const raw = isPercent ? cents((coveredTotal * value) / 100) : value;
  const cap = Math.min(coveredTotal, invoiceLeft);
  return { amount: Math.max(0, Math.min(raw, cap)), coveredTotal, capped: raw > cap };
}
